import './FirebaseLists2.css';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAuth } from '../../authNew/authSlice';
import { BasePageContainer, BaseCard, BaseVerticalContainer, BaseComponentContainer } from '../../../common/viewBaseComponents';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Dialog } from 'primereact/dialog';
import { Divider } from 'primereact/divider';

export default function FirebaseLists4() {
    const auth = useSelector(selectAuth);
    const [musicData, setMusicData] = useState({}); // State per i dati musicali
    const [filteredAlbums, setFilteredAlbums] = useState({}); // State per gli album filtrati
    const [searchTerm, setSearchTerm] = useState(''); // State per il termine di ricerca
    const [dialogVisible, setDialogVisible] = useState(false);
    const [dialogContent, setDialogContent] = useState('');
    const [dialogHeader, setDialogHeader] = useState('');
    
    // State per la ricerca avanzata
    const [advancedFilters, setAdvancedFilters] = useState({
        albumName: '',
        artist: '',
        trackName: '',
        trackPath: '',
        trackFilename: ''
    });

    // Carica i dati dal file JSON
    useEffect(() => {
        fetch('/music/music_db_decades.json') //fetch('/music/music_db_decades2.json')
            .then(response => response.json())
            .then(data => {
                setMusicData(data);
                setFilteredAlbums(data); // Inizialmente tutti gli album sono mostrati
            })
            .catch(error => {
                console.error('Errore nel caricamento dei dati musicali:', error);
            });
    }, []);

    // Funzione per cercare in tutti i campi del JSON
    const searchInObject = (obj, searchTerm) => {
        const term = searchTerm.toLowerCase();
        for (const key in obj) {
            if (typeof obj[key] === 'string') {
                if (obj[key].toLowerCase().includes(term)) {
                    return true;
                }
            } else if (typeof obj[key] === 'object' && obj[key] !== null) {
                if (searchInObject(obj[key], searchTerm)) {
                    return true;
                }
            }
        }
        return false;
    };

    // Funzione per filtrare in modo mirato
    const filterAlbums = (data, term) => {
        if (term.trim() === '' || term.length < 3) {
            return data;
        }

        const filtered = {};

        for (const albumName in data) {
            const album = data[albumName];
            const filteredTracks = album.tracks.filter(track => searchInObject(track, term));

            if (filteredTracks.length > 0) {
                // Aggiungi l'album solo se contiene tracce corrispondenti
                filtered[albumName] = {
                    ...album,
                    tracks: filteredTracks
                };
            }
        }

        return filtered;
    };

    // Funzione per filtrare con criteri avanzati
    const filterAlbumsAdvanced = (data, filters) => {
        // Controlla se almeno un filtro è compilato
        const hasActiveFilters = Object.values(filters).some(value => value.trim() !== '');
        if (!hasActiveFilters) {
            return data;
        }

        const filtered = {};

        for (const albumName in data) {
            const album = data[albumName];
            
            // Filtra per nome album
            if (filters.albumName.trim() !== '' && !albumName.toLowerCase().includes(filters.albumName.toLowerCase())) {
                continue;
            }

            const filteredTracks = album.tracks.filter(track => {
                // Filtra per artista
                if (filters.artist.trim() !== '' && !track.artist.toLowerCase().includes(filters.artist.toLowerCase())) {
                    return false;
                }

                // Filtra per nome brano
                if (filters.trackName.trim() !== '' && !track.cleanTitle.toLowerCase().includes(filters.trackName.toLowerCase())) {
                    return false;
                }

                // Filtra per path track
                if (filters.trackPath.trim() !== '' && (!track.path || !track.path.toLowerCase().includes(filters.trackPath.toLowerCase()))) {
                    return false;
                }

                // Filtra per filename track
                if (filters.trackFilename.trim() !== '' && (!track.filename || !track.filename.toLowerCase().includes(filters.trackFilename.toLowerCase()))) {
                    return false;
                }

                return true;
            });

            if (filteredTracks.length > 0) {
                filtered[albumName] = {
                    ...album,
                    tracks: filteredTracks
                };
            }
        }

        return filtered;
    };

    // Filtra i dati musicali in base al termine di ricerca o ai filtri avanzati
    useEffect(() => {
        // Controlla se almeno un filtro avanzato è compilato
        const hasAdvancedFilters = Object.values(advancedFilters).some(value => value.trim() !== '');
        
        // Se ci sono filtri avanzati compilati, usa la ricerca avanzata
        if (hasAdvancedFilters) {
            const filtered = filterAlbumsAdvanced(musicData, advancedFilters);
            setFilteredAlbums(filtered);
        } else if (searchTerm.trim() === '') {
            // Se nessun filtro è attivo, mostra tutti i dati
            setFilteredAlbums(musicData);
        } else {
            // Altrimenti usa la ricerca generica
            const filtered = filterAlbums(musicData, searchTerm);
            setFilteredAlbums(filtered);
        }
    }, [searchTerm, musicData, advancedFilters]);

    const showDialog = (content) => {
        setDialogContent(content);
        setDialogHeader('Dettagli');
        setDialogVisible(true);
    };

    const hideDialog = () => {
        setDialogVisible(false);
        setDialogContent('');
        setDialogHeader('');
    };

    // Helper function to check if a value should be displayed
    const shouldDisplayValue = (value) => {
        return value !== null && value !== undefined && value !== '';
    };

    // Controllo autenticazione
    if (!auth.isLogged) {
        return (
            <BasePageContainer>
                <Card title="Accesso Negato">
                    <p>Devi essere autenticato per visualizzare questa pagina.</p>
                </Card>
            </BasePageContainer>
        );
    }

    return (
        <BasePageContainer className={'w-12 flipleft'}>
            <BaseComponentContainer>
                <BaseVerticalContainer>
                    <Card title="Ricerca Musica - Decadi" className={'w-12'}>
                        <div className="p-mb-3">
                            <InputText
                                id="search"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Cerca per album, artista, traccia, ecc."
                                className="p-d-block search-input-responsive"
                            />
                        </div>

                        {/* Accordion per la ricerca avanzata */}
                        <Accordion className="p-mb-3">
                            <AccordionTab header="Ricerca Avanzata">
                                <div className="p-grid p-fluid">
                                    <div className="p-col-12 p-md-6 p-mb-3">
                                        <label htmlFor="albumNameFilter" className="p-d-block p-mb-2"><strong>Nome Album</strong></label>
                                        <InputText
                                            id="albumNameFilter"
                                            value={advancedFilters.albumName}
                                            onChange={(e) => setAdvancedFilters({...advancedFilters, albumName: e.target.value})}
                                            placeholder="Nome album"
                                        />
                                    </div>

                                    <div className="p-col-12 p-md-6 p-mb-3">
                                        <label htmlFor="artistFilter" className="p-d-block p-mb-2"><strong>Artista</strong></label>
                                        <InputText
                                            id="artistFilter"
                                            value={advancedFilters.artist}
                                            onChange={(e) => setAdvancedFilters({...advancedFilters, artist: e.target.value})}
                                            placeholder="Nome artista"
                                        />
                                    </div>

                                    <div className="p-col-12 p-md-6 p-mb-3">
                                        <label htmlFor="trackNameFilter" className="p-d-block p-mb-2"><strong>Nome Brano</strong></label>
                                        <InputText
                                            id="trackNameFilter"
                                            value={advancedFilters.trackName}
                                            onChange={(e) => setAdvancedFilters({...advancedFilters, trackName: e.target.value})}
                                            placeholder="Nome brano"
                                        />
                                    </div>

                                    <div className="p-col-12 p-md-6 p-mb-3">
                                        <label htmlFor="trackPathFilter" className="p-d-block p-mb-2"><strong>Path Track</strong></label>
                                        <InputText
                                            id="trackPathFilter"
                                            value={advancedFilters.trackPath}
                                            onChange={(e) => setAdvancedFilters({...advancedFilters, trackPath: e.target.value})}
                                            placeholder="Percorso track"
                                        />
                                    </div>

                                    <div className="p-col-12 p-md-6 p-mb-3">
                                        <label htmlFor="trackFilenameFilter" className="p-d-block p-mb-2"><strong>File Name Track</strong></label>
                                        <InputText
                                            id="trackFilenameFilter"
                                            value={advancedFilters.trackFilename}
                                            onChange={(e) => setAdvancedFilters({...advancedFilters, trackFilename: e.target.value})}
                                            placeholder="Nome file track"
                                        />
                                    </div>

                                    <div className="p-col-12 p-mb-3">
                                        <Button
                                            label="Resetta Filtri"
                                            icon="pi pi-times"
                                            onClick={() => setAdvancedFilters({
                                                albumName: '',
                                                artist: '',
                                                trackName: '',
                                                trackPath: '',
                                                trackFilename: ''
                                            })}
                                            className="p-button-secondary"
                                        />
                                    </div>
                                </div>
                            </AccordionTab>
                        </Accordion>

                        {Object.keys(filteredAlbums).length > 0 ? (
                            Object.entries(filteredAlbums).map(([albumName, albumData]) => (
                                <BaseCard key={`${albumName}`} className="p-p-3">
                                    <div className="p-d-flex p-flex-column w-full">
                                        {/* Album as accordion: tracks shown only when album is expanded */}
                                        <Accordion className="w-full">
                                            <AccordionTab header={<strong style={{ fontSize: '1.5rem' }}>{albumName}</strong>}>
                                                 {/* Render tracks */}
                                        <div className="p-ml-2">
                                            <Accordion className="w-full">
                                                {albumData.tracks.map((track, trackIndex) => (
                                                    <AccordionTab
                                                       key={`${albumName}-${trackIndex}`}
                                                       header={`${track.trackNumber} - ${track.artist} - ${track.cleanTitle}`}
                                                       showHeaderIcon={false}
                                                       //style={{ margin: '4px', padding: '3px' }}
                                                    >
                                                         <div className="p-ml-2" style={{ fontSize: '0.9rem' }}>
                                                             {shouldDisplayValue(track.filename) && (
                                                                 <div><strong>Nome file:</strong> {track.filename}</div>
                                                             )}
                                                             {shouldDisplayValue(track.path) && (
                                                                 <div><strong>Percorso:</strong> {track.path}</div>
                                                             )}
                                                             {shouldDisplayValue(track.artist) && (
                                                                 <div><strong>Artista:</strong> {track.artist}</div>
                                                             )}
                                                             {shouldDisplayValue(track.cleanTitle) && (
                                                                 <div><strong>Titolo:</strong> {track.cleanTitle}</div>
                                                             )}
                                                             {shouldDisplayValue(track.originalAlbum) && (
                                                                 <div><strong>Album Originale:</strong> {track.originalAlbum}</div>
                                                             )}
                                                         </div>
                                                     </AccordionTab>
                                                ))}
                                            </Accordion>
                                        </div>
                                            </AccordionTab>
                                        </Accordion>
                                    </div>
                                </BaseCard>
                            ))
                        ) : (
                            <Card>
                                <p>Nessun risultato trovato.</p>
                            </Card>
                        )}
                    </Card>
                </BaseVerticalContainer>
            </BaseComponentContainer>
            <Dialog header={dialogHeader} visible={dialogVisible} style={{ width: '80vw' }} onHide={hideDialog}>
                <div className="p-d-flex p-flex-column p-gap-3">
                    <pre style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>{dialogContent}</pre>
                </div>
            </Dialog>
        </BasePageContainer>
    );
}
